import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { profileAPI } from "../services/api";
import "./Applications.scss";

function statusToDisplay(status) {
  if (!status) return "Submitted";
  const s = String(status).toLowerCase().replace(/\s+/g, "-");
  const map = {
    submitted: "Submitted",
    "under-review": "Under Review",
    interview: "Under Review",
    shortlisted: "Shortlisted",
    rejected: "Rejected",
    accepted: "Hired",
    hired: "Hired",
  };
  return map[s] || "Submitted";
}

function Applications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await profileAPI.getApplications();
        if (res.data?.success && res.data?.data?.applications) {
          const list = res.data.data.applications.map((app) => {
            const pos = app.position && typeof app.position === "object" ? app.position : null;
            const id = pos?._id ?? (typeof app.positionId === "object" ? app.positionId?._id : app.positionId);
            return {
              id: app._id,
              positionId: id,
              jobTitle: pos?.title ?? "—",
              company: pos?.company ?? "—",
              location: pos?.location,
              employmentType: pos?.employmentType,
              status: app.status,
              statusDisplay: statusToDisplay(app.status),
              appliedDate: app.createdAt,
            };
          });
          setApplications(list);
        }
      } catch (err) {
        console.error("Applications load error:", err);
        setError(err.response?.data?.message || "Failed to load applications");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getStatusColor = (statusDisplay) => {
    const colors = {
      Submitted: "var(--primary)",
      "Under Review": "#F59E0B",
      Shortlisted: "var(--secondary)",
      Rejected: "#EF4444",
      Hired: "var(--secondary)",
    };
    return colors[statusDisplay] || "#6B7280";
  };

  if (loading) {
    return (
      <>
        <Header />
        <section className="applications-section">
          <div className="tf-container">
            <header className="applications-header">
              <h1 className="applications-title">My Jobs</h1>
              <p className="applications-subtitle">All positions you have applied to</p>
            </header>
            <div className="applications-loading">
              <div className="app-spinner" />
              <p>Loading applications...</p>
            </div>
          </div>
        </section>
        <Footer />
        <Gotop />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <section className="applications-section">
          <div className="tf-container">
            <header className="applications-header">
              <h1 className="applications-title">My Jobs</h1>
              <p className="applications-subtitle">All positions you have applied to</p>
            </header>
            <div className="applications-error">
              <p>{error}</p>
              <button type="button" className="btn-retry" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </div>
        </section>
        <Footer />
        <Gotop />
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="applications-section">
        <div className="applications-bg" aria-hidden="true" />
        <div className="tf-container">
          <header className="applications-header">
            <h1 className="applications-title">My Jobs</h1>
            <p className="applications-subtitle">All positions you have applied to</p>
          </header>

          {applications.length > 0 ? (
            <div className="applications-list">
              {applications.map((app) => (
                <article key={app.id} className="application-card">
                  <div className="application-card-main">
                    <div className="application-avatar" aria-hidden="true">
                      {app.company && app.company !== "—"
                        ? app.company.charAt(0).toUpperCase()
                        : "?"}
                    </div>
                    <div className="application-details">
                      <h2 className="application-job-title">{app.jobTitle}</h2>
                      <p className="application-company">{app.company}</p>
                      {(app.location || app.employmentType) && (
                        <p className="application-meta">
                          {[app.location, app.employmentType].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      <p className="application-date">
                        Applied on{" "}
                        {app.appliedDate
                          ? new Date(app.appliedDate).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                    <div className="application-actions">
                      <span
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(app.statusDisplay) }}
                      >
                        {app.statusDisplay}
                      </span>
                      {app.positionId && (
                        <Link
                          to={`/careers/${app.positionId}/apply`}
                          className="btn-view-application"
                        >
                          View details
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="applications-empty">
              <div className="applications-empty-icon" aria-hidden="true">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <p>You haven&apos;t applied to any jobs yet</p>
              <Link to="/careers" className="btn-browse-jobs">
                Browse Jobs
              </Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
      <Gotop />
    </>
  );
}

export default Applications;
